/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import flow from 'lodash.flow';
import { getLogger } from 'src/core/services/telemetry/telemetryLogger';
import { RootStore } from 'src/core/stores/RootStore';
import { HandlerGroupFactoryDeps, HandlerParams } from 'src/core/types/handler';
import { addDirtyState } from 'src/core/utils/decorators/addDirtyState';
import { getSimpleEntityService, SimpleEntityWithChildrenInput } from 'src/luis/core/services/entities/simpleEntityService';
import { getLuisFeatureService } from 'src/luis/core/services/luisFeatureService';
import { addGlobalFeaturesToNewModels } from 'src/luis/core/utils/addGlobalFeaturesToNewModels';
import { featureStoreToFeatureSwagger } from 'src/luis/modules/features/handlers/mappers';
import { Feature } from 'src/luis/modules/features/types/Feature';
import { getModelNameFromModelId } from 'src/luis/modules/features/utils/conversationFeatureHandlerHelpers';
import { featureSwaggerToConversationFeatureStore } from 'src/luis/modules/features/utils/conversationFeatureUtils';
import { deleteDanglingFeaturePointer } from 'src/luis/modules/features/utils/deleteDanglingFeaturePointer';
import { SimpleEntity } from 'src/luis/projects/entities/types/SimpleEntity';
import { instanceOfDeNormalizer } from 'src/luis/projects/entities/utils/entitiesDenormalizer';
import { getEntityHandlerGroupUtils } from 'src/luis/projects/entities/utils/entityHelpers';
import { LuisId } from 'src/luis/types/Resource';
import { EntityStore } from 'src/luis/types/stores/entities/EntityStore';
import { SimpleEntityStore } from 'src/luis/types/stores/entities/SimpleEntityStore';

export type SimpleEntityHandlerGroup = ReturnType<typeof createSimpleEntityHandlerGroup>;

/**
 * Represents a handler group for simple entity operations.
 *
 * @param dependencies The handler group dependencies.
 */
export const createSimpleEntityHandlerGroup = (dependencies: HandlerGroupFactoryDeps) => {
    const { rootStore } = dependencies;
    const { addOrReplaceEntityStore, getEntityStore, getAppVersionStore } = getEntityHandlerGroupUtils(rootStore);
    const logger = getLogger();
    /**
     * Loads a simple entity with the given id.
     *
     * @param id The id of the entity to load.
     */
    const loadSimpleEntity = async ({ id, context }: HandlerParams<Pick<SimpleEntityStore, 'id'>>) => {
        const { appId, versionId } = context;
        const simpleEntityService = getSimpleEntityService({ context });
        const versionStore = getAppVersionStore({ appId, versionId });
        const logTimedActivity = logger.createTimedActivityLogger<SimpleEntityStore>('LoadSimpleEntity', () => ({
            ...context,
            entityId: id
        }));

        const simpleEntityStore = await logTimedActivity(simpleEntityService.getEntity({ id }));

        simpleEntityStore.children.forEach(c => instanceOfDeNormalizer(c, versionStore.entityStoreHouse.items));

        addOrReplaceEntityStore({ appId, versionId }, simpleEntityStore);
    };

    /**
     * Creates a new simple entity.
     *
     * @param name The simple entity name.
     */
    const createSimpleEntity = async ({
        name,
        features,
        children,
        context,
        tracker
    }: HandlerParams<Pick<SimpleEntity, 'name' | 'features' | 'children'>>) =>
        flow(async () => {
            const { appId, versionId } = context;
            const simpleEntityService = getSimpleEntityService({ context });
            const versionStore = getAppVersionStore({ appId, versionId });

            const logTimedActivity = logger.createTimedActivityLogger<SimpleEntityStore>('CreateSimpleEntity', (result, error) => {
                const logData = { ...context, entityName: name, children };

                return error ? logData : { ...logData, entityId: result.id };
            });

            // Creates the entity
            const entityStore = await logTimedActivity(simpleEntityService.createEntity({ name, children }));
            addGlobalFeaturesToNewModels(entityStore, appId, versionId, rootStore);

            entityStore.children.forEach(c => instanceOfDeNormalizer(c, versionStore.entityStoreHouse.items));
            versionStore.entityStoreHouse.add(entityStore);

            // Create a map of entity name and features array to add.
            const entityFeatureMap = new Map<string, Feature[]>();
            entityFeatureMap.set(name, features);
            populateEntityFeatureMap(children, entityFeatureMap);

            const featureService = getLuisFeatureService({ context });
            await addFeaturesForEntity(entityStore, entityFeatureMap, featureService, appId, versionId, rootStore);

            if (tracker) {
                tracker.setContext(entityStore);
            }
        }, addDirtyState({ appId: context.appId, versionId: context.versionId, rootStore }))();

    /**
     * Creates a new simple entity (from an EntityStore)
     *
     * @param name The simple entity name.
     */
    const createSimpleEntityV2 = async ({
        name,
        children,
        context,
        tracker
    }: HandlerParams<Pick<SimpleEntityStore, 'name' | 'children'>>) =>
        flow(async () => {
            const { appId, versionId } = context;
            const simpleEntityService = getSimpleEntityService({ context });
            const versionStore = getAppVersionStore({ appId, versionId });

            const logTimedActivity = logger.createTimedActivityLogger<SimpleEntityStore>('createSimpleEntityV2', (result, error) => {
                const logData = { ...context, entityName: name, children };

                return error ? logData : { ...logData, entityId: result.id };
            });

            // Creates the entity
            const entityStore = await logTimedActivity(simpleEntityService.createEntityV2({ name, children }));

            addGlobalFeaturesToNewModels(entityStore, appId, versionId, rootStore);

            entityStore.children.forEach(c => instanceOfDeNormalizer(c, versionStore.entityStoreHouse.items));
            versionStore.entityStoreHouse.add(entityStore);

            if (tracker) {
                tracker.setContext(entityStore);
            }
        }, addDirtyState({ appId: context.appId, versionId: context.versionId, rootStore }))();

    /**
     * Creates a new simple child entity.
     *
     * @param name The simple entity name.
     */
    const createSimpleChildEntity = async ({
        parentId,
        context,
        name,
        absoluteParentId,
        ...entity
    }: HandlerParams<{ absoluteParentId: LuisId; parentId: LuisId; name: string } & SimpleEntityWithChildrenInput>) =>
        flow(async () => {
            const { appId, versionId } = context;
            const simpleEntityService = getSimpleEntityService({ context });

            const logTimedActivity = logger.createTimedActivityLogger<SimpleEntityStore>('createSimpleChildEntity', (result, error) => {
                const logData = { ...context, entityName: name };

                return error ? logData : { ...logData, entityId: result.id };
            });

            // Create the new child entity
            const { id, ...entityData } = await logTimedActivity(
                simpleEntityService.createNodeChildEntity({
                    name,
                    children: entity.children,
                    parentId
                })
            );

            // Get the root store
            const rootEntityStore = <SimpleEntityStore>getEntityStore({ appId, versionId, entityId: absoluteParentId });
            // Get the parent store starting from the root entity store
            const parentEntityStore = rootEntityStore.id === parentId ? rootEntityStore : rootEntityStore.getChildById(parentId);
            // Add the new child entity to its parent store
            parentEntityStore.addChild(id, entityData);
            // Update UI
            rootEntityStore.incrementUpdateCount();
        }, addDirtyState({ appId: context.appId, versionId: context.versionId, rootStore }))();

    /**
     * Deletes a given simple entity.
     *
     * @param id The id of the simple entity to delete.
     */
    const deleteSimpleEntity = async ({ id, context, tracker }: HandlerParams<Pick<EntityStore, 'id'>>) =>
        flow(async () => {
            if (tracker) {
                tracker.setContext(id);
            }

            const { appId, versionId } = context;
            const simpleEntityService = getSimpleEntityService({ context });
            const {
                entityStoreHouse: { items: entityStores },
                intentStoreHouse: { items: intentStores }
            } = rootStore.currentLuisAppStore.currentAppVersionStore;

            const logTimedActivity = logger.createTimedActivityLogger('DeleteSimpleEntity', () => ({ ...context, entityId: id }));

            await logTimedActivity(simpleEntityService.deleteEntity({ id }));

            // Deletes the dangling pointers for the models that uses these entities as a feature
            deleteDanglingFeaturePointer({ referencedFeatureId: id, intentStores, entityStores });

            getAppVersionStore({ appId, versionId }).entityStoreHouse.removeById(id);
        }, addDirtyState({ appId: context.appId, versionId: context.versionId, rootStore }))();

    /**
     * Deletes a given simple child entity.
     *
     * @param parentId The id of the entity's parent.
     * @param id The id of the simple entity to delete.
     */
    const deleteSimpleChildEntity = async ({
        id,
        parentId,
        context
    }: HandlerParams<Pick<SimpleEntityStore, 'id'> & { parentId: LuisId }>) =>
        flow(async () => {
            const { appId, versionId } = context;
            const simpleEntityService = getSimpleEntityService({ context });
            const logTimedActivity = logger.createTimedActivityLogger('deleteSimpleChildEntity', () => ({
                ...context,
                entityId: id,
                parentId
            }));

            await logTimedActivity(simpleEntityService.deleteEntity({ id }));

            const entityStore = <SimpleEntityStore>getEntityStore({ appId, versionId, entityId: parentId });

            entityStore.removeChildById(id);
            entityStore.incrementUpdateCount();
        }, addDirtyState({ appId: context.appId, versionId: context.versionId, rootStore }))();

    /**
     * Renames the given simple entity.
     *
     * @param id The id of the simple entity to update.
     * @param name The simple entity new name.
     */
    const renameSimpleEntity = async ({ id: entityId, name: newName, context }: HandlerParams<Pick<SimpleEntityStore, 'id' | 'name'>>) => {
        const { appId, versionId } = context;
        const simpleEntityService = getSimpleEntityService({ context });
        const logTimedActivity = logger.createTimedActivityLogger('RenameSimpleEntity', () => ({ ...context, entityId, newName }));

        await logTimedActivity(simpleEntityService.renameEntity({ id: entityId, name: newName }));

        const entityStore = <SimpleEntityStore>getEntityStore({ appId, versionId, entityId });
        entityStore.rename(newName);
        entityStore.incrementUpdateCount();
    };

    /**
     * Renames the given child entity.
     *
     * @param id The id of the simple child entity to rename.
     * @param name The simple child entity's new name.
     */
    const renameSimpleChildEntity = async ({
        id: entityId,
        name: newName,
        parentId,
        context
    }: HandlerParams<Pick<SimpleEntityStore, 'id' | 'name'> & { parentId: LuisId }>) => {
        const { appId, versionId } = context;
        const simpleEntityService = getSimpleEntityService({ context });

        const logTimedActivity = logger.createTimedActivityLogger('RenameSimpleChildEntity', () => ({
            ...context,
            parentId,
            entityId,
            newName
        }));

        await logTimedActivity(simpleEntityService.renameEntity({ id: entityId, name: newName }));

        const entityStore = <SimpleEntityStore>getEntityStore({ appId, versionId, entityId: parentId });
        entityStore.getChildById(entityId).rename(newName);
        entityStore.incrementUpdateCount();
    };

    /**
     * Updates/ deletes the instanceOf field of a leaf simple child entity.
     *
     * @param id The id of the simple child entity to update.
     * @param instanceOf The new instanceOf value to update the the child entity with (null for deletion).
     */
    const updateInstanceOf = async ({
        id: entityId,
        instanceOf,
        parentId,
        context
    }: HandlerParams<Pick<SimpleEntityStore, 'id'> & { parentId: LuisId; instanceOf: string }>) =>
        flow(async () => {
            const { appId, versionId } = context;
            const simpleEntityService = getSimpleEntityService({ context });
            const versionStore = getAppVersionStore({ appId, versionId });
            const entityStore = <SimpleEntityStore>getEntityStore({ appId, versionId, entityId: parentId });

            const logTimedActivity = logger.createTimedActivityLogger('UpdateInstanceOf', () => ({
                ...context,
                parentId,
                entityId,
                instanceOf
            }));

            await logTimedActivity(simpleEntityService.updateChildEntity({ id: entityId, instanceOf }));

            const childEntityStore = entityStore.getChildById(entityId);
            childEntityStore.updateInstanceOf(instanceOf);
            instanceOfDeNormalizer(childEntityStore, versionStore.entityStoreHouse.items);
        }, addDirtyState({ appId: context.appId, versionId: context.versionId, rootStore }))();

    return {
        loadSimpleEntity,
        createSimpleEntity,
        createSimpleEntityV2,
        createSimpleChildEntity,
        renameSimpleEntity,
        renameSimpleChildEntity,
        deleteSimpleEntity,
        deleteSimpleChildEntity,
        updateInstanceOf
    };
};

/**
 * A utility function that maps entity names to features
 * recursively.
 *
 * @todo Can be deprecated when backend includes a call to
 * create features with entity.
 *
 * @param newChildren The children to map.
 * @param map The map to update.
 */
const populateEntityFeatureMap = (newChildren: SimpleEntity[], map: Map<string, Feature[]>) => {
    newChildren.forEach(child => {
        map.set(child.name, child.features);
        populateEntityFeatureMap(child.children, map);
    });
};

/**
 * Adds features to the given entity store by calling the backend
 * to create them and on success.
 *
 * @param entityStore The store that was created.
 * @param entityFeatureMap The mapping between the entity
 * children and their features.
 * @param featureService The feature service that contains
 * the function to update the features.
 * @param appId The current app id.
 * @param versionId The current version id.
 * @param rootStore The root store that contains all app info.
 */
const addFeaturesForEntity = async (
    entityStore: SimpleEntityStore,
    entityFeatureMap: Map<string, Feature[]>,
    featureService: ReturnType<typeof getLuisFeatureService>,
    appId: LuisId,
    versionId: string,
    rootStore: RootStore
) => {
    const features = entityFeatureMap.get(entityStore.name);
    const featureStores = features.map(featureSwaggerToConversationFeatureStore);
    const featureSwaggers = features.map(feature =>
        featureStoreToFeatureSwagger({
            name: getModelNameFromModelId({ appId, versionId, featureId: feature.id, rootStore }),
            typeId: feature.typeId === 'feature' ? 'feature' : 'model',
            isRequired: false
        })
    );

    if (featureSwaggers.length > 0) {
        await featureService.updateFeatures({ model: entityStore, features: featureSwaggers });
        entityStore.featureStoreHouse.setItems(featureStores);
    }

    for (const child of entityStore.children) {
        await addFeaturesForEntity(child, entityFeatureMap, featureService, appId, versionId, rootStore);
    }
};
